import router from '@/router';
import { message } from 'ant-design-vue';
import type { Module } from '@/types/artwork/generate';
import type { ImageCommonState, ColourImageState } from '@/types/image';
import { useEventTracking } from '@/hooks/useEventTracking';
import type { DirectiveBindingValue } from '@/hooks/useEventTracking/eventTracking';
import { downloadFile, jsonParse, sleep, getFileExtension } from '@/utils';
import { useArtworkStore } from '@/store/artwork';
import { defaultModelId, defaultFluxModelId } from '@/assets/artwork';
import { isExpansionModuleId } from '@/assets/artwork/moduleEnum';
import { FREE_PERSPECTIVE_MODULE_IDS } from '@/assets/artwork/options';
import { updateArtworkState } from '@/views/workspace/hooks';
import _, { cloneDeep } from 'lodash';
import { favoriteImage } from '@/views/space/service';
import { $bus } from '@/mitt';
import { useUserStore } from '@/store/user';
import { getClientId, useGenerate } from '@/hooks/useGenerate';
import { handleSuperImg, imageHasTransparent } from '@/utils/image';
import { deepUser } from '../service';
import { useTeamEditionStore } from '@/store/teamEdition';
import { useModelViewerStore, WorkType } from '@/store/modelViewer';
import { getModelLatestInfo } from '@/components/Model/common';
import { getUpscaleOptionsByImageSize } from '@/assets/artwork/limits';
import { logger } from '@/utils/logger';
import { isLoginRequiredError } from '@/utils/requireLogin';

export function updateFavoriteStatus(
  imageState: ImageCommonState,
  eventValue?: DirectiveBindingValue['eventValue']
) {
  return new Promise<void>(async (resolve, reject) => {
    const nextStatus = imageState.collect === 1 ? 0 : 1;

    try {
      await favoriteImage({
        imgId: imageState.id as number,
        exampleId: imageState.exampleId,
        collect: nextStatus
      });
      resolve();
      if (!imageState.isHideMessage) {
        message.success(nextStatus === 1 ? '收藏成功' : '取消收藏成功');
      }

      $bus.emit('favoriteUpdate', {
        id: imageState.id,
        collect: nextStatus
      });

      eventValue && useEventTracking({
        event: 'click',
        eventValue,
        imageAction: true
      });
    } catch (error) {
      reject(error);
      if (!isLoginRequiredError(error)) {
        message.error(nextStatus === 1 ? '收藏失败' : '取消收藏失败');
      }
    }
  })
}

/**
 * 更新3D模型收藏状态
 * @param imageState 
 * @returns 
 */
export function updateThreeDCollectStatus(
  imageState: ImageCommonState,
) {
  return new Promise<void>(async (resolve, reject) => {
    const nextStatus = imageState.collect === 1 ? 0 : 1;
    try {
      await favoriteImage({
        imgId: imageState.id as number,
        collect: nextStatus
      });
      resolve();
      if (!imageState.isHideMessage) {
        message.success(nextStatus === 1 ? '收藏成功' : '取消收藏成功');
      }

      $bus.emit('favoriteUpdate', {
        id: imageState.id,
        collect: nextStatus
      });
    } catch (error) {
      reject(error);
      if (!isLoginRequiredError(error)) {
        message.error('收藏失败');
      }
    }

  })
}

// 辅助函数：检查模块是否在store中存在
function isStoreModule(moduleId: ModuleIds) {
  return moduleId !== ModuleId.Palette;
}

/**
 * 为 Qwen 自由视角模块的回填参数补充 freeCanvasSource 字段
 * 一键成稿入口（reuse/iterate/useInference）都视为从作品详情进入
 *
 * 同时将嵌套在 qwenFreePerspectiveParams 中的 viewAngleId / viewAngleSource
 * 提取到顶层，以匹配 store 的 freePerspectiveParams 扁平结构。
 * 历史生图参数中这两字段存于 qwenFreePerspectiveParams 内，
 * 而 store 使用顶层字段，浅合并时会丢失导致复用编辑后视角高亮错误。
 */
export function ensureFreePerspectiveEntrySource(data: any) {
  if (!data) return data;
  const moduleId = Number(data.module);
  if (moduleId === ModuleId.QwenFreePerspective) {
    if (!data.qwenFreePerspectiveParams) {
      data.qwenFreePerspectiveParams = {};
    }
    data.qwenFreePerspectiveParams.freeCanvasSource = 'artwork_detail';

    // 从嵌套的 qwenFreePerspectiveParams 中提取 viewAngleId / viewAngleSource 到顶层
    // 使后续 resetModuleParams 的浅合并能正确覆盖 store 中对应字段
    if (data.qwenFreePerspectiveParams.viewAngleId !== undefined) {
      data.viewAngleId = data.qwenFreePerspectiveParams.viewAngleId;
    }
    if (data.qwenFreePerspectiveParams.viewAngleSource !== undefined) {
      data.viewAngleSource = data.qwenFreePerspectiveParams.viewAngleSource;
    }
  }
  return data;
}

export async function useInference(
  module: Module,
  imageState: ImageCommonState,
  param: string | null = null,
  eventValue: DirectiveBindingValue['eventValue']
) {
  const artworkStore = useArtworkStore();
  const data = objExt.parse<any>(param, {});
  const moduleKey = getModuleKeyById(module);
  const moduleRouteKey = getModuleRouteKeyById(module);
  if (!moduleKey || !moduleRouteKey) {
    return;
  }

  // 检查是否为palette模块，palette模块不在store中管理状态
  if (module === ModuleId.Palette) {
    console.warn('palette模块不支持useInference操作');
    return;
  }

  let modelList = data?.modelList ?? [];
  const isFlux = modelList[0]?.ckptType === 'Flux';

  if (isFlux && [ModuleId.Edit].includes(module)) {
    return message.warn('HOLO - V1模型不支持在智能编辑生图模块使用');
  }

  // 该图片为线稿提取生成
  if (data?.module === ModuleId.Sketch) {
    let modelId = 0;

    if ([ModuleId.Explore, ModuleId.Fission].includes(module)) { // 相似图裂变
      modelId = ['production', 'gray'].includes(import.meta.env.VITE_ENV) ? 139 : 184;
    } else {
      modelId = defaultModelId;
    }

    modelList = [{ id: modelId }];
  } else if (router.currentRoute.value.meta.group !== 'workspace' && module === ModuleId.Edit) {
    modelList = [{ id: defaultModelId }];
  }

  // 发送3d模型，相当于上传一张图，可以进行去除背景和生成3d模型
  const handleThreeTransform = async () => {
    artworkStore.resetModuleParams(module, { ...threeTransformParams });
    const modelViewerStore = useModelViewerStore();
    modelViewerStore.resetModelData()
    modelViewerStore.workType = WorkType.IMAGE
    modelViewerStore.imageUrl = imageState.image || '';
  }

  // 在作品内部
  if (router.currentRoute.value.meta.group === 'workspace') {
    // 智能编辑器
    if (module === ModuleId.ThreeTransform) {
      handleThreeTransform();
    } else {
      // 使用类型保护确保模块在store中存在
      if (isStoreModule(module)) {
        const oldModuleParams = (artworkStore as any)[moduleKey].params;

        if (Array.isArray(data?.modelList)) {
          await artworkStore.updateArtwork({
            modelList
          }, {
            edit: true,
            updateTag: false
          });
        }
        oldModuleParams.layoutRefer = imageState;
        // 自由视角的提示词为内部生成的视角描述，不应在“用作参考图”时暴露给其他模块
        const sourceModuleId = Number(data?.module);
        const isFreePerspectiveSource = FREE_PERSPECTIVE_MODULE_IDS.includes(sourceModuleId as (typeof FREE_PERSPECTIVE_MODULE_IDS)[number]);
        oldModuleParams.prompt = isFreePerspectiveSource ? '' : (data?.prompt || '');
        ensureFreePerspectiveEntrySource(oldModuleParams);
        artworkStore.resetModuleParams(module, { ...oldModuleParams });
      }
    }
  } else {
    artworkStore.resetArtwork();
    if (data && Array.isArray(data.modelList)) {
      await artworkStore.updateArtwork({
        modelList
      }, {
        updateTag: false
      });
    }

    if (module === 12) {
      handleThreeTransform()
    } else {
      const sourceModuleId = Number(data?.module);
      const isFreePerspectiveSource = FREE_PERSPECTIVE_MODULE_IDS.includes(sourceModuleId as (typeof FREE_PERSPECTIVE_MODULE_IDS)[number]);
      artworkStore.resetModuleParams(module, {
        prompt: isFreePerspectiveSource ? '' : data?.prompt,
        layoutRefer: imageState
      });
    }
  }

  if (module === ModuleId.Replace) {
    artworkStore.resetModuleResult(ModuleId.Replace as Module);
  }

  // 如果是扩图，则需要触发图片推词
  if (isExpansionModuleId(module)) {
    artworkStore.expansion.params.historyJsonUrl = '';
    artworkStore.expansion.params.aspectRatios = '0:0';

    // 自动填充背景
    const { autoFillBackground } = useAutoFillBackground();
    const res = await autoFillBackground(artworkStore.expansion.params.layoutRefer.image);
    artworkStore.expansion.params.layoutRefer = res || artworkStore.expansion.params.layoutRefer;
    console.log('用作参考图时，扩图自动填充背景', artworkStore.expansion.params.layoutRefer);

    // // 判断来源模块为扩图再特殊处理引用图
    // if (data.module === ModuleId.Expansion) {
    //   artworkStore.expansion.params.layoutRefer = { ...artworkStore.expansion.params.originLayoutRefer }
    // }
    (async () => {
      const componentStore = useComponentStore()
      //  判断智能扩图模块是否已加载，如果不是，轮询判断10次
      if (!componentStore.getComponentStatus('expansion-panel')?.isLoaded) {
        console.log('智能扩图模块未加载，等待1S后再次判断')
        for (let i = 0; i < 10; i++) {
          await sleep(1000)
          if (componentStore.getComponentStatus('expansion-panel')?.isLoaded) {
            $bus.emit('startPhotoTweet', module);
            break;
          }
        }
      } else {
        $bus.emit('startPhotoTweet', module);
      }
    })()

  }

  router.push({
    name: moduleRouteKey,
    query: {
      reference: 'true'
    }
  });

  useEventTracking({
    event: 'click',
    eventValue,
    imageAction: true
  });
}

/** 将非 PNG 格式的 Blob 转为 PNG Blob，兼容浏览器 Clipboard API 限制 */
async function convertBlobToPng(blob: Blob): Promise<Blob> {
  const img = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) resolve(result);
      else reject(new Error('Canvas toBlob failed'));
    }, 'image/png');
  });
}

// 复制图片
export function copyImage(image: string, eventValue: DirectiveBindingValue['eventValue']) {
  return new Promise<void>(async (resolve, reject) => {
    try {

      const ext = getFileExtension(image);
      if (ext !== 'png' && ext !== 'jpg' && ext !== 'jpeg') {
        message.error('仅支持复制PNG/JPG格式图片');
        reject();
        return;
      }

      const { blob: imgBlob } = await generateWatermarkedBlob(image);
      // 浏览器 Clipboard API 不支持 image/jpeg 写入，需要转为 PNG
      let pngBlob: Blob;
      if (imgBlob.type === 'image/png') {
        pngBlob = imgBlob;
      } else {
        pngBlob = await convertBlobToPng(imgBlob);
      }
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlob })
      ]);
      message.success('已复制');
      resolve();
    } catch (error) {
      message.error('复制失败');
      console.error('复制图片失败', error);
      reject();
    } finally {
      useEventTracking({
        event: 'click',
        eventValue,
        imageAction: true
      });

      deepUser(image);
    }
  })
}

// 下载图片
import { useDownloadImageStore } from '@/store/downloadImage';
import { threeTransformParams } from '@/assets/artwork/module/threeTransform';
import { useAutoFillBackground } from '@/hooks/useAutoFillBackground';
import { useComponentStore } from '@/store/componentStore';
import { downloadWatermarkedImage, generateWatermarkedBlob } from '@/utils/downloadWatermarkedImage';
import { getModuleKeyById, getModuleRouteKeyById, getModuleRouteKeyByKey, objExt, getImageInfo } from '@xmiles/holopix-helper';
import { ModuleId, ModuleIds } from '@xmiles/holopix-types';
import { geneDownload } from '../service';

// ============================================================
// 辅助函数
// ============================================================

/**
 * 获取当前用户的模型 ID
 * @description 优先使用用户最近使用的模型，若已删除则返回默认模型
 */
export async function getUserModelId(): Promise<number> {
  try {
    const userStore = useUserStore();
    const teamEditionStore = useTeamEditionStore();

    const data = await userStore.getCustom(teamEditionStore.userType === 'personal' ? 'model' : 'teamModel');

    // 没有最近使用的模型
    if (!data) {
      return defaultModelId;
    }

    // 判断这个模型是否被删除
    const { deleted, modelInfo } = await getModelLatestInfo(parseInt(data));

    // 最近使用的模型已经被删除，返回默认模型
    if (deleted) {
      return modelInfo?.ckptType === 'Flux' ? defaultFluxModelId : defaultModelId;
    }

    return parseInt(data);
  } catch (error) {
    return defaultModelId;
  }
}

/**
 * 图片下载功能
 * @param id - 图片ID（生图id）
 * @param image - 图片URL
 * @param size - 下载尺寸倍数(1为原图,>1为放大)
 * @param artworkVersionId - 作品版本id（可选）
 * @param eventValue - 事件追踪参数
 * @returns Promise
 */

//  快速任务 ● 下载 ● 放大
export function downloadImage({
  id,
  image,
  size,
  removeBg,
  superImgType,
  artworkVersionId,
  clientId: clientIdOverride,
}: {
  id: number;
  image: string;
  size: number;
  removeBg: boolean;
  superImgType: string;
  artworkVersionId?: number;
  /** 画布/会话消息侧优先传入，与 geneDownload 打点一致 */
  clientId?: string;
},
  eventValue?: DirectiveBindingValue['eventValue']
) {
  return new Promise<void>(async (resolve, reject) => {
    // 获取下载状态管理store
    const downloadImageStore = useDownloadImageStore();

    try {
      // 触发下载时，调用后端接口记录下载行为（无效 geneId 不传，避免 0 误报）
      geneDownload({
        ...(id > 0 ? { geneId: id } : {}),
        artworkVersionId,
        clientId: clientIdOverride || getClientId(),
        type: 1
      }).catch(() => {
        // 记录下载失败不影响实际下载流程
      });

      // 如果有事件追踪参数,记录点击事件
      eventValue && useEventTracking({
        event: 'click',
        eventValue,
        imageAction: true
      });

      // 记录用户行为
      deepUser(image);

      // 原图下载流程
      if (size === 1 && !removeBg) {
        // 添加下载记录
        downloadImageStore.addDownload();
        const downloadUrl = image;

        try {
          // 直接下载原图
          await downloadWatermarkedImage(downloadUrl);
          resolve();
          downloadImageStore.updateDownload('downloadSuccess');
        } catch (error) {
          logger.error('原图下载失败', { error, downloadUrl });
          downloadImageStore.updateDownload('downloadError');
          reject(error);
        }
      } else {
        // 放大图片下载流程 - 校验倍数是否合法
        let finalSize = size;

        // 如果需要放大，校验倍数是否支持
        if (size > 1) {
          try {
            let imgWidth = 0;
            let imgHeight = 0;
            try {
              const info = await getImageInfo(image);
              if (info.width > 0 && info.height > 0) {
                imgWidth = info.width;
                imgHeight = info.height;
              }
            } catch {
              // ignore
            }
            if (imgWidth === 0 || imgHeight === 0) {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              await new Promise<void>((imgResolve, imgReject) => {
                img.onload = () => { imgWidth = img.width; imgHeight = img.height; imgResolve(); };
                img.onerror = () => imgReject(new Error('加载图片失败'));
                img.src = image;
              });
            }

            const upscaleOptions = getUpscaleOptionsByImageSize(imgWidth, imgHeight);

            if (!upscaleOptions.supportedFactors.includes(size)) {
              console.warn(`[downloadImage] 图片尺寸 ${imgWidth}x${imgHeight} 不支持 ${size} 倍放大，降级为原图下载`);
              finalSize = 1;
            }
          } catch (error) {
            // 获取图片尺寸失败，降级为原图下载
            console.warn('[downloadImage] 获取图片尺寸失败，降级为原图下载:', error);
            finalSize = 1;
          }
        }

        // 如果降级为原图且不需要去背景，直接走原图下载
        if (finalSize === 1 && !removeBg) {
          downloadImageStore.addDownload();
          try {
            await downloadWatermarkedImage(image);
            resolve();
            downloadImageStore.updateDownload('downloadSuccess');
          } catch (error) {
            logger.error('降级原图下载失败', { error, image });
            downloadImageStore.updateDownload('downloadError');
            reject(error);
          }
          return;
        }

        const userStore = useUserStore();
        // 添加生成记录
        downloadImageStore.addGenerate();

        try {
          let params;

          if (removeBg && finalSize === 1) {
            params = {
              userId: userStore.userInfo.id,
              module: 10,
              originImgUrl: image,
              apiClientId: getClientId(),
            };
          } else if (removeBg) {
            params = {
              userId: userStore.userInfo.id,
              module: 21,
              threeDClientId: getClientId(),
              threeDOriginImgUrl: image,
              rescaleFactor: finalSize,
              superImgType: 'person', // 图片类型 cartoon、person
            };
          } else {
            params = {
              module: 9, // 放大模块ID
              userId: userStore.userInfo.id,
              originImgUrl: image,
              rescaleFactor: finalSize,
              apiClientId: getClientId(),
              superImgType: superImgType, // 图片类型 cartoon、person
            };
          }

          const res = await handleSuperImg(params);

          const downloadUrl = res?.data?.imgUrl;
          if (typeof downloadUrl === 'string' && downloadUrl) {
            // 生成完成，进入下载阶段
            downloadImageStore.updateDownload('downloading');
            await downloadWatermarkedImage(downloadUrl);
            resolve();
            downloadImageStore.updateDownload('downloadSuccess');
          }
        } catch (error) {
          logger.error('放大图片生成失败', { error });
          downloadImageStore.updateDownload('generateError');
          reject(error);
        }
      }
    } catch (error) {
      logger.error('下载失败', { error });
      message.error('下载失败');
      reject(error);
    }
  });
}

// 一键同款
export async function createIdentical(params: any) {
  const userStore = useUserStore();

  if (params) {
    // 非会员，生图数据只能只能1
    if (userStore.isExpireUser || userStore.highestPackage == 'free') {
      params.batchSize = 1;
    }

    const artworkStore = useArtworkStore();

    artworkStore.resetArtwork();

    artworkStore.resetModuleParams(params.module, params);

    let modelList = (params.modelList || []).map((item: any, index: number) => {
      return {
        ...item,
        strength: params.loraList[index]?.strength,
      };
    });

    // 如果无模型，则使用默认模型
    if (modelList.length === 0) {
      const data = await getUserModelId()
      modelList = [{ id: data }];
    }

    await artworkStore.updateArtwork({
      modelList: modelList
    });

    router.push({
      name: getModuleRouteKeyById(params.module as ModuleIds),
    });
  }
}

// 再迭代
export async function iterate(
  param: string | null = null,
  imageState: ColourImageState,
  eventValue: DirectiveBindingValue['eventValue']
) {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  await userStore.queryUserRights();

  const data = jsonParse(param);

  if (!data) {
    return;
  }

  // 之前生图时使用了快速通道，但是会员已失效
  if (data.vipChannel && userStore.highestPackage === 'free') {
    // 切换成标准通道
    artworkStore.channel = 'normal';
  }

  let artworkId = data.artworkId;
  let modelList = data.modelList || [];

  if (!modelList.length) {
    modelList = artworkStore.artworkState.modelList || [];
  }

  if (!artworkId) {
    artworkId = artworkStore.artworkState.id;
  }

  if (artworkStore.artworkState.id !== data.artworkId) {
    artworkStore.resetArtwork();
  }

  await artworkStore.updateArtwork({
    id: artworkId,
    modelList
  }, {
    updateTag: false,
  });

  ensureFreePerspectiveEntrySource(data);
  artworkStore.resetModuleParams(data.module as Module, data);
  artworkStore.resetModuleResult(data.module as Module);

  if (router.currentRoute.value.name !== 'colour') {
    artworkStore.skipResetParamsModules = [data.module];
    artworkStore.skipResetResultModules = [data.module];
  }

  // 作品不同时，刷新页面更新模块数据
  if (artworkStore.artworkState.id !== data.artworkId) {
    await updateArtworkState();
  }

  await router.push({
    name: 'colour'
  });

  if (data) {
    const obj = cloneDeep(data);
    obj.layoutRefer = {
      id: imageState.id,
      image: imageState.image,
      width: imageState.width,
      height: imageState.height,
    };
    obj.source = 2; // 1: 直接生成 2: 再迭代
    obj.batchSize = 1; // 生图数量固定为1
    obj.batchRedraw = false; // 关闭批量验证
    if (imageState.redrawWeight) {
      obj.redrawWeight = imageState.redrawWeight; // 设置为该图片的重绘幅度
    }

    artworkStore.resetModuleParams(3, { ...obj });

    artworkStore.startGenerate(3);

    useEventTracking({
      event: 'click',
      eventValue,
      imageAction: true
    });
  }
}